import { isQueryOnlyNavigation } from '~/utils/isQueryOnlyNavigation';
import log from '~/utils/log';

const isSameInternalPath = (target: string, currentPath: string, baseUrl: string) => {
    const targetUrl = new URL(target, baseUrl);
    const currentUrl = new URL(currentPath, baseUrl);

    return targetUrl.origin === currentUrl.origin && targetUrl.pathname === currentUrl.pathname;
};

/**
 * Keeps users who have given the Academy consent off the consent fallback story.
 *
 * The gating itself lives in `docebo-session`, where a refused session signals the missing consent.
 * That does not apply here: the fallback story is not a locked page, so no session is minted and the
 * account status has to be queried directly.
 */
export default defineNuxtRouteMiddleware(async (to, from) => {
    /** The consent does not change when only the query of the page changes. */
    if (isQueryOnlyNavigation(to, from)) return;

    /** Let editors preview the fallback story, mirroring `redirectLockedPages.global.ts`. */
    const { isDraftModeEnabled } = useDraftMode();
    if (isDraftModeEnabled.value) return;

    const sectionConfig = useCurrentSectionConfig(to).value;
    if (sectionConfig?.sectionId !== 'academy') return;

    const auth = useAuth();
    if (!auth.value) return;

    const { data: config, error: configError } = await useConfig(to);
    if (configError.value || !config.value?.content) {
        log.error('[academy-consent-check] Failed to load config, skipping consent check', configError.value);
        return;
    }

    /** Consent only applies to sections backed by Docebo. */
    if (!config.value.content.lockedPagesRequireDoceboSession) return;

    const runtimeConfig = useRuntimeConfig();
    const fallbackUrl = useNoConsentFallbackUrl(config.value.content, to);
    if (!fallbackUrl || !isSameInternalPath(fallbackUrl, to.fullPath, runtimeConfig.public.baseUrl)) return;

    const { data: consentStatus } = await useAcademyConsentStatus();
    if (!isAcademyConsentGiven(consentStatus.value)) return;

    const academyHomeUrl = useAcademyHomeUrl(to).value;
    if (isSameInternalPath(academyHomeUrl, to.fullPath, runtimeConfig.public.baseUrl)) return;

    log.debug(`[academy-consent-check] Consent already given, redirecting to Academy home: ${academyHomeUrl}`);

    /** `replace` keeps the fallback story out of the history, so Back does not re-trigger this. */
    return navigateTo(academyHomeUrl, { replace: true });
});
